import { useState, useEffect } from "react";
import { ERAS } from "./HistoryScreen";
import { COMPOSERS } from "./ComposersScreen";
import { ETIQUETTE_ITEMS } from "./Etiquette";
import { composerImages } from "../data/composerImages";
import { PEOPLE } from "./PeopleScreen";
import { peopleImages } from "../data/peopleImages";

const ERA_LABELS = {
  medieval: "중세", renaissance: "르네상스",
  baroque: "바로크", classical: "고전주의", romantic: "낭만주의", modern: "현대",
};
const ERA_PERIODS = {
  medieval: "500 — 1450", renaissance: "1420 — 1600",
  baroque: "1600 — 1750", classical: "1750 — 1820",
  romantic: "1820 — 1900", modern: "1900 — 현재",
};

const ERA_BG = {
  baroque: "linear-gradient(160deg, #3D2B1F 0%, #7A5C2E 55%, #C4A040 100%)",
  classical: "linear-gradient(160deg, #1C3A4E 0%, #2A5840 60%, #4A9070 100%)",
  romantic: "linear-gradient(160deg, #2E1535 0%, #6B2D5E 65%, #A05A82 100%)",
  modern: "linear-gradient(160deg, #151E30 0%, #1E3A5F 65%, #2D6090 100%)",
};
const ERA_LETTER_COLOR = {
  baroque: "#F0E4C4", classical: "#C8EAD8", romantic: "#F4D0E8", modern: "#C8D8F0",
};

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Noto+Serif+KR:wght@300;400;500;600&family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&display=swap');
  * { margin: 0; padding: 0; box-sizing: border-box; }
  :root {
    --cream: #F7F3ED; --warm-white: #FDFAF6;
    --gold: #B8962E; --gold-light: #D4AF6A; --gold-pale: #F0E4C4;
    --charcoal: #2C2820; --brown-mid: #6B5D4F; --brown-light: #A89880;
    --border: #DDD4C0; --shadow: rgba(44,40,32,0.08);
  }
  body { font-family: 'Noto Serif KR', serif; background: var(--cream); color: var(--charcoal); min-height: 100vh; }

  .nav { position: sticky; top: 0; z-index: 100; background: rgba(247,243,237,0.92); backdrop-filter: blur(12px); border-bottom: 1px solid var(--border); padding: 0 48px; display: flex; align-items: center; justify-content: space-between; height: 64px; }
  .nav-logo { font-family: 'Cormorant Garamond', serif; font-size: 22px; font-weight: 400; letter-spacing: 0.06em; color: var(--charcoal); cursor: pointer; }
  .nav-menu { display: flex; gap: 36px; list-style: none; }
  .nav-menu li { font-size: 14px; letter-spacing: 0.04em; color: var(--brown-mid); cursor: pointer; position: relative; padding-bottom: 2px; transition: color 0.2s; }
  .nav-menu li::after { content: ''; position: absolute; bottom: 0; left: 0; width: 0; height: 1px; background: var(--gold); transition: width 0.3s; }
  .nav-menu li:hover { color: var(--charcoal); }
  .nav-menu li:hover::after { width: 100%; }
  .nav-menu li.active { color: var(--gold); font-weight: 600; }
  .nav-menu li.active::after { width: 100%; }

  .profile-hero { background: var(--warm-white); border-bottom: 1px solid var(--border); padding: 48px 24px 40px; text-align: center; }
  .profile-avatar { width: 80px; height: 80px; border-radius: 50%; background: var(--gold-pale); border: 2px solid var(--gold-light); margin: 0 auto 20px; display: flex; align-items: center; justify-content: center; font-family: 'Cormorant Garamond', serif; font-size: 36px; color: var(--gold); }
  .profile-title { font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 600; color: var(--charcoal); margin-bottom: 6px; }
  .profile-sub { font-size: 13px; font-weight: 300; color: var(--brown-mid); letter-spacing: 0.03em; margin-bottom: 28px; }
  .stats-row { display: flex; justify-content: center; gap: 48px; }
  .stat-item { text-align: center; }
  .stat-num { font-family: 'Playfair Display', serif; font-size: 26px; font-weight: 600; color: var(--gold); margin-bottom: 4px; }
  .stat-label { font-size: 11px; letter-spacing: 0.1em; color: var(--brown-light); text-transform: uppercase; font-weight: 300; }

  .section { max-width: 860px; margin: 0 auto; padding: 40px 24px 0; }
  .section-title { font-family: 'Playfair Display', serif; font-size: 18px; font-weight: 600; color: var(--charcoal); margin-bottom: 20px; display: flex; align-items: center; gap: 12px; }
  .section-title::after { content: ''; flex: 1; height: 1px; background: var(--border); max-width: 60px; }

  .progress-box { background: var(--warm-white); border: 1px solid var(--border); border-radius: 4px; padding: 24px 28px; margin-bottom: 40px; }
  .progress-item { margin-bottom: 16px; }
  .progress-item:last-child { margin-bottom: 0; }
  .progress-label { display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 13px; color: var(--brown-mid); }
  .progress-count { font-size: 12px; color: var(--brown-light); }
  .progress-track { height: 5px; background: var(--gold-pale); border-radius: 99px; overflow: hidden; }
  .progress-fill { height: 100%; background: linear-gradient(90deg, var(--gold), var(--gold-light)); border-radius: 99px; transition: width 1.2s cubic-bezier(0.4,0,0.2,1); }

  .fav-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; margin-bottom: 40px; }
  .fav-card { background: var(--warm-white); border: 1px solid var(--border); border-radius: 4px; display: flex; align-items: center; gap: 14px; cursor: pointer; transition: border-color 0.2s, box-shadow 0.2s; overflow: hidden; position: relative; }
  .fav-card:hover { border-color: var(--gold-light); box-shadow: 0 4px 16px var(--shadow); }

  .fav-thumb { width: 64px; height: 64px; flex-shrink: 0; position: relative; overflow: hidden; }
  .fav-thumb img { width: 100%; height: 100%; object-fit: cover; object-position: center top; display: block; }
  .fav-thumb-fallback { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-family: 'Cormorant Garamond', serif; font-size: 26px; }

  .fav-info { padding: 14px 14px 14px 0; flex: 1; min-width: 0; }
  .fav-name { font-size: 14px; font-weight: 500; color: var(--charcoal); margin-bottom: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .fav-sub { font-size: 12px; font-weight: 300; color: var(--brown-light); }

  .era-fav-card { background: var(--warm-white); border: 1px solid var(--border); border-radius: 4px; padding: 18px 20px; display: flex; align-items: center; gap: 14px; cursor: pointer; transition: border-color 0.2s, box-shadow 0.2s; position: relative; }
  .era-fav-card:hover { border-color: var(--gold-light); box-shadow: 0 4px 16px var(--shadow); }
  .fav-remove { position: absolute; top: 6px; right: 6px; background: none; border: none; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--brown-light); font-size: 15px; border-radius: 50%; transition: background 0.15s, color 0.15s; line-height: 1; }
  .fav-remove:hover { background: rgba(44,40,32,0.09); color: var(--charcoal); }
  .era-icon { width: 40px; height: 40px; border-radius: 2px; background: var(--gold-pale); border: 1px solid var(--border); display: flex; align-items: center; justify-content: center; font-family: 'Cormorant Garamond', serif; font-size: 20px; color: var(--gold); flex-shrink: 0; }

  .quiz-score-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-bottom: 40px; }
  .quiz-score-card { background: var(--warm-white); border: 1px solid var(--border); border-radius: 4px; padding: 20px 20px 16px; cursor: pointer; transition: border-color 0.2s, box-shadow 0.2s; }
  .quiz-score-card:hover { border-color: var(--gold-light); box-shadow: 0 4px 16px var(--shadow); }
  .quiz-score-icon { font-size: 20px; margin-bottom: 10px; }
  .quiz-score-label { font-size: 13px; font-weight: 500; color: var(--charcoal); margin-bottom: 10px; }
  .quiz-score-num { font-family: 'Playfair Display', serif; font-size: 28px; font-weight: 700; color: var(--gold); line-height: 1; margin-bottom: 4px; }
  .quiz-score-sub { font-size: 11px; color: var(--brown-light); font-weight: 300; letter-spacing: 0.03em; margin-bottom: 10px; }
  .quiz-score-bar-track { height: 4px; background: var(--gold-pale); border-radius: 99px; overflow: hidden; }
  .quiz-score-bar-fill { height: 100%; background: linear-gradient(90deg, var(--gold), var(--gold-light)); border-radius: 99px; transition: width 1.2s cubic-bezier(0.4,0,0.2,1); }

  .empty-box { text-align: center; padding: 36px 24px; background: var(--warm-white); border: 1px dashed var(--border); border-radius: 4px; margin-bottom: 40px; }
  .empty-icon { font-size: 28px; margin-bottom: 10px; opacity: 0.45; }
  .empty-text { font-size: 14px; font-weight: 300; color: var(--brown-light); margin-bottom: 4px; }
  .empty-hint { font-size: 12px; font-weight: 300; color: var(--brown-light); opacity: 0.7; }

  footer { border-top: 1px solid var(--border); padding: 28px 48px; display: flex; justify-content: space-between; align-items: center; margin-top: 40px; }
  .footer-logo { font-family: 'Cormorant Garamond', serif; font-size: 18px; color: var(--brown-light); letter-spacing: 0.05em; }
  .footer-text { font-size: 12px; color: var(--brown-light); font-weight: 300; letter-spacing: 0.03em; }

  @media (max-width: 768px) {
    .nav { padding: 0 20px; } .nav-menu { display: none; }
    .stats-row { gap: 16px; }
    .fav-grid { grid-template-columns: 1fr; }
    .quiz-score-grid { grid-template-columns: 1fr; }
    footer { flex-direction: column; gap: 12px; text-align: center; padding: 24px 20px; margin-bottom: 62px; }
  }
`;

// 퀴즈 진행 바 기준값 (이 횟수 이상이면 바 꽉 참)
const QUIZ_BAR_MAX = 10;

export default function MyPageScreen({ onNavigate }) {
  const navigate = onNavigate || (() => { });
  const [filled, setFilled] = useState(false);
  const [favEras, setFavEras] = useState([]);
  const [favComposers, setFavComposers] = useState([]);
  const [favPeople, setFavPeople] = useState([]);
  const [favEtiqette, setFavEtiquette] = useState([]);
  const [visitedEras, setVisitedEras] = useState([]);
  const [visitedComposers, setVisitedComposers] = useState([]);
  const [visitedEtiquette, setVisitedEtiquette] = useState([]);

  // 퀴즈 푼 횟수
  const [quizComposerCount, setQuizComposerCount]   = useState(0);
  const [quizHistoryCount, setQuizHistoryCount]     = useState(0);
  const [quizEtiquetteCount, setQuizEtiquetteCount] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setFilled(true), 300);
    try {
      setFavEras(JSON.parse(localStorage.getItem("fav_eras") || "[]"));
      setFavComposers(JSON.parse(localStorage.getItem("fav_composers") || "[]"));
      setFavPeople(JSON.parse(localStorage.getItem("fav_people") || "[]"));
      setFavEtiquette(JSON.parse(localStorage.getItem("fav_etiquette") || "[]"));
      setVisitedEras(JSON.parse(localStorage.getItem("visited_eras") || "[]"));
      setVisitedComposers(JSON.parse(localStorage.getItem("visited_composers") || "[]"));
      setVisitedEtiquette(JSON.parse(localStorage.getItem("visited_etiquette") || "[]"));

      // 퀴즈 푼 횟수 로컬스토리지에서 읽기
      setQuizComposerCount(parseInt(localStorage.getItem("quiz_composer_count") || "0", 10));
      setQuizHistoryCount(parseInt(localStorage.getItem("quiz_history_count") || "0", 10));
      setQuizEtiquetteCount(parseInt(localStorage.getItem("quiz_etiquette_count") || "0", 10));
    } catch { }
    return () => clearTimeout(t);
  }, []);

  const progressData = [
    { label: "역사", done: quizHistoryCount, total: ERAS.length },
    { label: "작곡가", done: quizComposerCount, total: COMPOSERS.length },
    { label: "에티켓", done: quizEtiquetteCount, total: ETIQUETTE_ITEMS.length },
  ];
  const totalLearned = visitedEras.length + visitedComposers.length + visitedEtiquette.length;
  const totalFav = favEras.length + favComposers.length + favPeople.length + favEtiqette.length;

  const quizScoreData = [
    { icon: "📖", label: "역사 퀴즈",   count: quizHistoryCount },
    { icon: "🎵", label: "작곡가 퀴즈", count: quizComposerCount },
    { icon: "✨", label: "에티켓 퀴즈", count: quizEtiquetteCount },
  ];

  const removeEra = (id, e) => {
    e.stopPropagation();
    const next = favEras.filter(x => x !== id);
    setFavEras(next);
    localStorage.setItem("fav_eras", JSON.stringify(next));
  };
  const removeComposer = (id, e) => {
    e.stopPropagation();
    const next = favComposers.filter(x => x !== id);
    setFavComposers(next);
    localStorage.setItem("fav_composers", JSON.stringify(next));
  };
  const removePerson = (id, e) => {
    e.stopPropagation();
    const next = favPeople.filter(x => x !== id);
    setFavPeople(next);
    localStorage.setItem("fav_people", JSON.stringify(next));
  };
  const removeEtiquette = (id, e) => {
    e.stopPropagation();
    const next = favEtiqette.filter(x => x !== id);
    setFavEtiquette(next);
    localStorage.setItem("fav_etiquette", JSON.stringify(next));
  };

  const goToComposer = (id) => {
    const composer = COMPOSERS.find(c => c.id === id);
    if (composer) navigate("composer-detail", composer);
  };
  const goToPerson = (id) => {
    const person = PEOPLE.find(p => p.id === id);
    if (person) navigate("people-detail", person);
  };

  return (
    <>
      <style>{style}</style>

      <nav className="nav">
        <div className="nav-logo" onClick={() => navigate("home")}>Music Life</div>
        <ul className="nav-menu">
          {["역사", "작곡가", "역대 인물", "에티켓", "내 페이지"].map((item, i) => {
            const pages = ["history", "composers", "people", "etiquette", "mypage"];
            return (
              <li key={item} className={pages[i] === "mypage" ? "active" : ""} onClick={() => navigate(pages[i])}>
                {item}
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="profile-hero">
        <div className="profile-avatar">♪</div>
        <div className="profile-title">클래식 탐험가</div>
        <div className="profile-sub">나만의 클래식 음악 여정</div>
        <div className="stats-row">
          <div className="stat-item">
            <div className="stat-num">{totalFav}</div>
            <div className="stat-label">즐겨찾기</div>
          </div>
          <div className="stat-item">
            <div className="stat-num">{totalLearned}</div>
            <div className="stat-label">학습 완료</div>
          </div>
          <div className="stat-item">
            <div className="stat-num">{visitedEras.length}</div>
            <div className="stat-label">탐험 시대</div>
          </div>
        </div>
      </div>

      <div className="section">
        <div className="section-title">학습 진도</div>
        <div className="progress-box">
          {progressData.map(({ label, done, total }) => (
            <div className="progress-item" key={label}>
              <div className="progress-label">
                <span>{label}</span>
                <span className="progress-count">{done}/{total} 완료</span>
              </div>
              <div className="progress-track">
                <div className="progress-fill" style={{ width: filled ? `${(done / total) * 100}%` : "0%" }} />
              </div>
            </div>
          ))}
        </div>

        {/* 퀴즈 현황 */}
        <div className="section-title">퀴즈 현황</div>
        <div className="quiz-score-grid">
          {quizScoreData.map(({ icon, label, count }) => (
            <div className="quiz-score-card" key={label}>
              <div className="quiz-score-icon">{icon}</div>
              <div className="quiz-score-label">{label}</div>
              <div className="quiz-score-num">{count}</div>
              <div className="quiz-score-sub">총 푼 횟수</div>
              <div className="quiz-score-bar-track">
                <div
                  className="quiz-score-bar-fill"
                  style={{ width: filled ? `${Math.min((count / QUIZ_BAR_MAX) * 100, 100)}%` : "0%" }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* 즐겨찾기 — 시대 */}
        <div className="section-title">즐겨찾기 시대</div>
        {favEras.length === 0 ? (
          <div className="empty-box">
            <div className="empty-icon">🏛️</div>
            <div className="empty-text">즐겨찾기한 시대가 없습니다</div>
            <div className="empty-hint">역사 페이지에서 시대를 즐겨찾기 해보세요</div>
          </div>
        ) : (
          <div className="fav-grid">
            {favEras.map(id => {
              const era = ERAS.find(e => e.id === id);
              if (!era) return null;
              const bg = ERA_BG[id] || "linear-gradient(160deg, #3D2B1F, #C4A040)";
              const letterColor = ERA_LETTER_COLOR[id] || "#F0E4C4";
              return (
                <div className="fav-card" key={id} onClick={() => navigate("history")}>
                  <div className="fav-thumb">
                    <div className="fav-thumb-fallback" style={{ background: bg }}>
                      <span style={{ color: letterColor, fontSize: 22, fontFamily: "'Cormorant Garamond', serif" }}>
                        {ERA_LABELS[id]?.[0] || "♪"}
                      </span>
                    </div>
                  </div>
                  <div className="fav-info">
                    <div className="fav-name">{ERA_LABELS[id] || id}</div>
                    <div className="fav-sub">{ERA_PERIODS[id] || ""}</div>
                  </div>
                  <button className="fav-remove" onClick={e => removeEra(id, e)}>×</button>
                </div>
              );
            })}
          </div>
        )}

        {/* 즐겨찾기 — 작곡가 */}
        <div className="section-title">즐겨찾기 작곡가</div>
        {favComposers.length === 0 ? (
          <div className="empty-box">
            <div className="empty-icon">🎵</div>
            <div className="empty-text">즐겨찾기한 작곡가가 없습니다</div>
            <div className="empty-hint">작곡가 페이지에서 즐겨찾기 해보세요</div>
          </div>
        ) : (
          <div className="fav-grid">
            {favComposers.map(id => {
              const composer = COMPOSERS.find(c => c.id === id);
              if (!composer) return null;
              const img = composerImages[id];
              const portrait = { baroque: ERA_BG.baroque, classical: ERA_BG.classical, romantic: ERA_BG.romantic, modern: ERA_BG.modern }[composer.era] || ERA_BG.baroque;
              const letterColor = ERA_LETTER_COLOR[composer.era] || "#F0E4C4";
              return (
                <div className="fav-card" key={id} onClick={() => goToComposer(id)}>
                  <div className="fav-thumb">
                    {img ? (
                      <img src={img} alt={composer.name} />
                    ) : (
                      <div className="fav-thumb-fallback" style={{ background: portrait }}>
                        <span style={{ color: letterColor }}>{composer.name[0]}</span>
                      </div>
                    )}
                  </div>
                  <div className="fav-info">
                    <div className="fav-name">{composer.name}</div>
                    <div className="fav-sub">{composer.years} · {composer.nat}</div>
                  </div>
                  <button className="fav-remove" onClick={e => removeComposer(id, e)}>×</button>
                </div>
              );
            })}
          </div>
        )}

        {/* 즐겨찾기 — 인물 */}
        <div className="section-title">즐겨찾기 인물</div>
        {favPeople.length === 0 ? (
          <div className="empty-box">
            <div className="empty-icon">🎹</div>
            <div className="empty-text">즐겨찾기한 인물이 없습니다</div>
            <div className="empty-hint">역대 인물 페이지에서 즐겨찾기 해보세요</div>
          </div>
        ) : (
          <div className="fav-grid">
            {favPeople.map(id => {
              const person = PEOPLE.find(p => p.id === id);
              if (!person) return null;
              const img = peopleImages[id];
              return (
                <div className="fav-card" key={id} onClick={() => goToPerson(id)}>
                  <div className="fav-thumb">
                    {img ? (
                      <img src={img} alt={person.name} />
                    ) : (
                      <div className="fav-thumb-fallback" style={{ background: "#1A2A4A" }}>
                        <span style={{ color: "#C8D8F0" }}>{person.name[0]}</span>
                      </div>
                    )}
                  </div>
                  <div className="fav-info">
                    <div className="fav-name">{person.name.replace(" ♡", "")}</div>
                    <div className="fav-sub">{person.years} · {person.role}</div>
                  </div>
                  <button className="fav-remove" onClick={e => removePerson(id, e)}>×</button>
                </div>
              );
            })}
          </div>
        )}

        {/* 즐겨찾기 — 에티켓 */}
        <div className="section-title">즐겨찾기 에티켓</div>
        {favEtiqette.length === 0 ? (
          <div className="empty-box">
            <div className="empty-icon">✨</div>
            <div className="empty-text">즐겨찾기한 에티켓이 없습니다</div>
            <div className="empty-hint">에티켓 페이지에서 즐겨찾기 해보세요</div>
          </div>
        ) : (
          <div className="fav-grid">
            {favEtiqette.map(id => {
              const item = ETIQUETTE_ITEMS.find(e => e.id === id);
              if (!item) return null;
              return (
                <div className="fav-card" key={id} onClick={() => navigate("etiquette")}>
                  <div className="fav-thumb">
                    <div className="fav-thumb-fallback" style={{ background: "var(--gold-pale)" }}>
                      <span style={{ fontSize: 24 }}>{item.emoji}</span>
                    </div>
                  </div>
                  <div className="fav-info">
                    <div className="fav-name">{item.title}</div>
                    <div className="fav-sub">{item.subtitle}</div>
                  </div>
                  <button className="fav-remove" onClick={e => removeEtiquette(id, e)}>×</button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <footer>
        <div className="footer-logo">Music Life</div>
        <div className="footer-text">클래식 음악의 아름다움을 함께 탐구합니다</div>
      </footer>
    </>
  );
}
